/**
 * A photograph on its way onto a card.
 *
 * A phone camera hands over a 12-megapixel JPEG. Read straight through
 * `FileReader.readAsDataURL` that becomes four to eight megabytes of base64
 * sitting in a DOM attribute, and everything downstream then fails in a way
 * nobody can read: html-to-image percent-encodes the whole card into one
 * enormous SVG data URL, the decode of it can simply never settle, and a card
 * that does survive lands over the backend's 4 MB ceiling and is refused. An
 * interview on 2026-09-18 hit exactly this — the picture never appeared and the
 * card did not render — so the shrink happens here, before any of it.
 *
 * 1400px on the long edge is comfortably more than the card's own 1080×1350 at
 * export scale, and JPEG at 0.82 puts a full-frame photograph at a few hundred
 * kilobytes.
 */
const MAX_EDGE = 1400;
const QUALITY = 0.82;

/** The old behaviour, kept as the floor: a file we cannot decode still goes through whole. */
function rawDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('could not read the file'));
    reader.readAsDataURL(file);
  });
}

async function bitmapOf(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // `from-image` is what keeps a photograph taken sideways the right way up;
  // an <img> already applies EXIF orientation itself.
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* fall through */ }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('could not decode the photograph'));
      img.src = url;
    });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

/**
 * A data URL small enough to live on a card. Never throws: a photograph that
 * will not decode is better carried through at full size than lost.
 */
export async function photoForCard(file: File, maxEdge = MAX_EDGE): Promise<string> {
  try {
    const src = await bitmapOf(file);
    const w = 'width' in src ? src.width : 0;
    const h = 'height' in src ? src.height : 0;
    if (!w || !h) return await rawDataUrl(file);
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return await rawDataUrl(file);
    ctx.drawImage(src as CanvasImageSource, 0, 0, canvas.width, canvas.height);
    if ('close' in src) src.close();
    const out = canvas.toDataURL('image/jpeg', QUALITY);
    // A canvas that produced nothing usable is not an improvement on the file.
    return out.startsWith('data:image/jpeg') && out.length > 'data:image/jpeg;base64,'.length ? out : await rawDataUrl(file);
  } catch (err) {
    console.warn('[kindling] could not shrink the photograph; sending it whole', err);
    return rawDataUrl(file);
  }
}
